import type { FunctionComponent } from 'react';
import { Fragment } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileComponent } from '~/shared/data-contract/job-profile';

import { ContentSections } from '../components/Common';
import { useTranslatedSectionTitle } from '../job-profile-section/use-translated-section-title';
import { useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { matchSections } from './comparison-utils';
import { DescriptionDiff } from './DescriptionDiff';

export const DescriptionComparisonSection: FunctionComponent<{
    readonly leftProfile: JobProfileComponent;
    readonly rightProfile: JobProfileComponent;
}> = ({ leftProfile, rightProfile }) => {
    const translate = useTranslate();
    const { activeLanguage } = useActiveLanguage();
    const resolveLeftTitle = useTranslatedSectionTitle(leftProfile.sectionTags);
    const resolveRightTitle = useTranslatedSectionTitle(rightProfile.sectionTags);

    if (leftProfile.structured === null) {
        return null;
    }

    const leftTranslation = leftProfile.translations.find((t) => t.language === activeLanguage);
    const rightTranslation = rightProfile.translations.find((t) => t.language === activeLanguage);

    const sections = matchSections(leftProfile, rightProfile, activeLanguage);

    return (
        <Fragment>
            <ContentSections columns="two">
                <div data-testid="title-section-diff-left">
                    <DescriptionDiff
                        against={leftTranslation?.title}
                        current={rightTranslation?.title}
                        title={translate('pages.job-architecture-v2.comparision.title-section')}
                        type="removed"
                    />
                </div>
                <div data-testid="title-section-diff-right">
                    <DescriptionDiff
                        against={leftTranslation?.title}
                        current={rightTranslation?.title}
                        title={translate('pages.job-architecture-v2.comparision.title-section')}
                        type="added"
                    />
                </div>
            </ContentSections>
            <ContentSections columns="two">
                {sections?.map(({ current, old }) => {
                    return (
                        <Fragment key={current.id ?? current.tag}>
                            {old === undefined ? (
                                <div /> /* grid placeholder */
                            ) : (
                                <div data-testid="description-diff-left">
                                    <DescriptionDiff
                                        against={old.value}
                                        current={current.value}
                                        title={resolveLeftTitle(old)}
                                        type="removed"
                                    />
                                </div>
                            )}
                            <div data-testid="description-diff-right">
                                <DescriptionDiff
                                    against={old?.value ?? ''}
                                    current={current.value}
                                    title={resolveRightTitle(current)}
                                    type="added"
                                />
                            </div>
                        </Fragment>
                    );
                })}
            </ContentSections>
        </Fragment>
    );
};
